import { Signal } from "signals";
import { FileLocation } from "slotworks/config/asset-config-schema";
import Contract from "slotworks/utils/contracts/contract";
import * as TweenJS from "slotworks/utils/tween";

export default class LoadbarComponent {

    public onChange: Signal;
    public onComplete: Signal;

    public currentPercent: number;

    public chaser: PIXI.Sprite;
    private bar: PIXI.DisplayObject;

    private totalKb: number;
    private completeKb: number;
    private msPerKb: number;
    private files: Map<string, FileLocation>;

    private loadingPercentage: PIXI.Text;
    private loadingLabel: PIXI.Text;
    private loadingSprite: PIXI.DisplayObject;

    private msSinceLoadStarted: number;

    private loadTween: TweenJS.Tween;

    /**
     * Creates a bar and scales it based on files being loaded in.
     * Optionally add a "chaser" graphic which always sits at the end of the bar
     *
     * @param bar {PIXI.DisplayObject}
     * @param files {FileLocation[]}
     * @param chaser {PIXI.Sprite}
     */
    public constructor(files: FileLocation[], bar?: PIXI.DisplayObject, chaser?: PIXI.Sprite) {
        this.onChange = new Signal();
        this.onComplete = new Signal();

        this.bar = bar;
        this.chaser = chaser;

        if (this.bar) {
            this.bar.scale.x = 0;
        }

        if (this.chaser) {
            this.chaser.anchor.set(0.75, 0.5);
            this.chaser.x = this.bar.x + this.bar.getBounds().width;
            this.chaser.y = this.bar.y + this.bar.getBounds().height * 0.5;
        }

        this.currentPercent = this.totalKb = this.completeKb = 0;
        this.msSinceLoadStarted = new Date().getTime();
        this.msPerKb = 3000;
        this.files = new Map<string, FileLocation>();

        for (const file of files) {

            let fileName = file.name;

            // When loading atlases only care about the image, not the json
            if (file.url.indexOf("@")) {
                fileName += "_image";
            }

            this.files.set(fileName, file);
            this.totalKb += file.size;
        }

        // Start off load bar with complete guess for how fast the client's internet connection is
        this.loadTween = new TweenJS.Tween(this)
            .to({ currentPercent: 0.25 }, 10000)
            .onUpdate(this.update)
            .onComplete(() => {
                this.nudgeTween();
            })
            .start();
    }

    public addLoadingText(label: PIXI.Text, percentage: PIXI.Text, loadingSprite?: PIXI.DisplayObject) {
        this.loadingLabel = label;
        this.loadingSprite = loadingSprite;
        this.loadingPercentage = percentage;
    }

    public fileLoaded(resource: PIXI.loaders.Resource) {

        // Ignore non registered files
        if (!this.files.has(resource.name)) {
            return;
        }

        // Stop last load tween
        if (this.loadTween) {
            this.loadTween.stop();
        }

        // Get file data
        const fileData = this.files.get(resource.name);

        // Update total kb loaded
        this.completeKb += fileData.size;

        // Update estimated times
        this.msPerKb = ((new Date().getTime()) - this.msSinceLoadStarted) / this.completeKb;
        const timeToComplete = (this.totalKb - this.completeKb) * this.msPerKb;

        // Try and finish load bar at estimated speed
        this.loadTween = new TweenJS.Tween(this)
            .to({ currentPercent: 1 }, timeToComplete)
            .onUpdate(() => { this.update(); })
            .start();

        this.onChange.dispatch();
    }

    /**
     * Quickly completes the loadbar and returns a Contract for when it's done
     */
    public loadComplete() {
        if (this.loadTween) {
            this.loadTween.stop();
        }

        return new Contract<void>((resolve) => {
            this.loadTween = new TweenJS.Tween(this)
                .to({ currentPercent: 1 }, 750)
                .onUpdate(() => { this.update(); })
                .onComplete(() => {

                    if (this.loadingPercentage) {
                        this.loadingPercentage.visible = false;
                    }
                    if (this.loadingLabel) {
                        this.loadingLabel.visible = false;
                    }
                    if (this.loadingSprite) {
                        this.loadingSprite.visible = false;
                    }

                    this.onComplete.dispatch();

                    this.destroy();
                    resolve(null);
                })
                .start();
        });
    }

    public destroy() {
        this.onChange.removeAll();
        this.onComplete.removeAll();

        this.loadTween.stop();
        this.loadTween = null;

        this.bar = null;
    }

    /**
     * To prevent load bar from grinding completely to a halt, if the initial guess load completes
     * before we have any download speed estimates, the bar should nudge along slowly
     */
    private nudgeTween() {
        let nudgeTarget;
        if (this.currentPercent < 0.97) {
            nudgeTarget = this.currentPercent + 0.01;
        }

        this.loadTween = new TweenJS.Tween(this)
            .to({ currentPercent: nudgeTarget }, 3000)
            .onUpdate(() => {
                this.update();
            })
            .onComplete(() => {
                this.nudgeTween();
            })
            .start();
    }

    private update() {
        if (this.bar) {
            this.bar.scale.x = Math.min(1, this.currentPercent);

            if (this.chaser) {
                this.chaser.x = this.bar.x + this.bar.getBounds().width;
                this.chaser.y = this.bar.y + this.bar.getBounds().height * 0.5;
            }
        }

        this.updateLabel();

        this.onChange.dispatch();
    }

    private updateLabel() {
        if (this.currentPercent < 1) {
            if (this.loadingPercentage) {
                this.loadingPercentage.text = Math.round(this.currentPercent * 100) + "%";
            }
        }
    }
}
